/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, FC } from 'react';
import {
  VictoryLine,
  VictoryAxis,
  VictoryChart,
  VictoryScatter,
  VictoryLabel,
  VictoryVoronoiContainer,
  VictoryGroup,
} from 'victory';
import { Text, View } from 'react-native';
import Colors from '../constants/Colors';
import { axisDateLabel, tooltipDateLabel, tickValSamples } from './axisUtils';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import { alternateLineColor, selectedLineColor } from './AirQualityGraph';
import { Metric } from '../constants/Types';
// import CustomPoint from './CustomPoint';

type Props = {
  dates: { start: any; end: any };
  compareDates: { start: any; end: any };
  title?: string;
  compareToggled: boolean;
  metric?: string,
  prevMetric?: string;
  prevTitle?: string;
};
const BarWithLineGraphWeb: FC<Props> = ({
  dates,
  compareDates,
  title,
  compareToggled,
  metric,
  prevMetric,
  prevTitle,
}) => {
  const [graphData, setGraphData] = useState([]);
  const [compareGraphData, setCompareGraphData] = useState([]);
  const [utilityData, setUtilityData] = useState([]);
  const [compareUtilityData, setCompareUtilityData] = useState([]);
  const [tickVals, setTickVals] = useState([]);
  const [tickVals2, setTickVals2] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 500 });

  const domainLimit: number = Math.min(1.3 * Math.max(
    ...(graphData ?? []).map((point) => point.value),
    ...(compareGraphData ?? []).map((point) => point.value),
    1,
  ), 100000000);

  const alternateDomainLimit: number = Math.min(1.3 * Math.max(
    ...(utilityData ?? []).map((point) => point.value),
    ...(compareUtilityData ?? []).map((point) => point.value),
    1,
  ), 100000000);

  const higherDomainLimit = Math.max(domainLimit, alternateDomainLimit);

  const yAxisTickFormat = (value: number, limit: number) => Math.round((limit > 2 ? 1 : 10) * value
    * (limit / higherDomainLimit)) / (limit > 2 ? 1 : 10);

  // eslint-disable-next-line no-unused-vars
  const [option, setOption] = useState(null);
  let point = {};
  let comparePoint = {};

  const Score = [];
  const Score2 = [];

  const averageMetrics: Metric[] = [
    Metric.TEMPERATURE, Metric.NOISE, Metric.PM25, Metric.TVOC, Metric.CO2, Metric.HUMIDITY,
    Metric.LIGHT,
  ];

  useEffect(() => {
    const tickArray = [...Array(Math.max(graphData?.length, utilityData?.length)).keys()];
    const tickArray2 = [...Array(Math.max(compareGraphData?.length, compareUtilityData?.length))
      .keys()];
    const tickArrayUtil = [];
    setTickVals(tickValSamples(tickArray, 7));
    setTickVals2(tickValSamples(tickArray2, 7));
    utilityData.forEach((i) => tickArrayUtil.push(i.value));
  }, [compareGraphData, graphData, utilityData, compareUtilityData]);

  useEffect(() => {
    const pointsArray = [];
    if (averageMetrics.includes(Metric[metric as keyof typeof Metric])) {
      GetMetric({
        metric, range: dates, aggregationFunction: AggregationFunction.AVG,
      })
        ?.then((data) => {
          if (data !== undefined) {
            data?.forEach((e, idx) => {
              Score.push(e.value);
              point = {
                value: Math.floor(e.value * 10) / 10,
                index: idx,
                date: e.timestamp,
                isCompare: false,
                x: idx,
                y: idx,
              };
              pointsArray.push(point);
            });
          }
          setGraphData(pointsArray);
        });
    } else if (metric === Metric.OCCUPANCY) {
      GetMetric({
        metric, range: dates, aggregationFunction: AggregationFunction.MAX,
      })
        ?.then((data) => {
          if (data !== undefined) {
            data?.forEach((e, idx) => {
              Score.push(e.value);
              point = {
                value: Math.floor(e.value * 10) / 10,
                index: idx,
                date: e.timestamp,
                isCompare: false,
                x: idx,
                y: idx,
              };
              pointsArray.push(point);
            });
          }
          setGraphData(pointsArray);
        });
    } else {
      GetMetric({
        metric, range: dates, aggregationFunction: AggregationFunction.SUM,
      })
        ?.then((data) => {
          if (data !== undefined) {
            data?.forEach((e, idx) => {
              Score.push(e.value);
              point = {
                value: Math.floor(e.value * 10) / 10,
                index: idx,
                date: e.timestamp,
                isCompare: false,
                x: idx,
                y: idx,
              };
              pointsArray.push(point);
            });
          }
          setGraphData(pointsArray);
        });
    }
  }, [dates.start, metric, prevMetric]);

  useEffect(() => {
    const comparePointsArray = [];
    if (averageMetrics.includes(Metric[metric as keyof typeof Metric])) {
      GetMetric({
        metric, range: compareDates, aggregationFunction: AggregationFunction.AVG,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              Score2.push(e.value);
              comparePoint = {
                value: Math.floor(e.value * 10) / 10,
                index,
                date: e.timestamp,
                isCompare: true,
              };
              comparePointsArray.push(comparePoint);
              index += 1;
            });
          }
          setCompareGraphData(comparePointsArray);
        });
    } else if (metric === Metric.OCCUPANCY) {
      GetMetric({
        metric, range: compareDates, aggregationFunction: AggregationFunction.MAX,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              Score2.push(e.value);
              comparePoint = {
                value: Math.floor(e.value * 10) / 10,
                index,
                date: e.timestamp,
                isCompare: true,
              };
              comparePointsArray.push(comparePoint);
              index += 1;
            });
          }
          setCompareGraphData(comparePointsArray);
        });
    } else {
      GetMetric({
        metric, range: compareDates, aggregationFunction: AggregationFunction.SUM,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              Score2.push(e.value);
              comparePoint = {
                value: Math.floor(e.value * 10) / 10,
                index,
                date: e.timestamp,
                isCompare: true,
              };
              comparePointsArray.push(comparePoint);
              index += 1;
            });
          }
          setCompareGraphData(comparePointsArray);
        });
    }
  }, [compareDates.start, metric]);

  useEffect(() => {
    const utilityArray = [];
    if (averageMetrics.includes(Metric[prevMetric as keyof typeof Metric])) {
      GetMetric({
        metric: prevMetric, range: dates, aggregationFunction: AggregationFunction.AVG,
      })
        ?.then((data) => {
          const index = 0;
          data?.forEach((e, idx) => {
            point = {
              name: index,
              value: Math.floor(e.value * 10) / 10,
              date: e.timestamp,
              x: index,
              index: idx,
            };
            utilityArray.push(point);
          });
          setUtilityData(utilityArray);
        });
    } else if (prevMetric === Metric.OCCUPANCY) {
      GetMetric({
        metric: prevMetric, range: dates, aggregationFunction: AggregationFunction.MAX,
      })
        ?.then((data) => {
          const index = 0;
          data?.forEach((e, idx) => {
            point = {
              name: index,
              value: Math.floor(e.value * 10) / 10,
              date: e.timestamp,
              x: index,
              index: idx,
            };
            utilityArray.push(point);
          });
          setUtilityData(utilityArray);
        });
    } else {
      GetMetric({
        metric: prevMetric, range: dates, aggregationFunction: AggregationFunction.SUM,
      })
        ?.then((data) => {
          const index = 0;
          data?.forEach((e, idx) => {
            point = {
              name: index,
              value: Math.floor(e.value * 10) / 10,
              date: e.timestamp,
              x: index,
              index: idx,
            };
            utilityArray.push(point);
          });
          setUtilityData(utilityArray);
        });
    }
  }, [dates.start, prevMetric]);

  useEffect(() => {
    const utilityArray = [];
    if (averageMetrics.includes(Metric[prevMetric as keyof typeof Metric])) {
      GetMetric({
        metric: prevMetric,
        range: compareDates,
        aggregationFunction: AggregationFunction.AVG,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              point = {
                name: index,
                value: Math.floor(e.value * 10) / 10,
                x: index,
                y: index,
                index,
              };
              utilityArray.push(point);
              index += 1;
            });
          }
          setCompareUtilityData(utilityArray);
        });
    } else if (prevMetric === Metric.OCCUPANCY) {
      GetMetric({
        metric: prevMetric,
        range: compareDates,
        aggregationFunction: AggregationFunction.MAX,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              point = {
                name: index,
                value: Math.floor(e.value * 10) / 10,
                x: index,
                y: index,
                index,
              };
              utilityArray.push(point);
              index += 1;
            });
          }
          setCompareUtilityData(utilityArray);
        });
    } else {
      GetMetric({
        metric: prevMetric,
        range: compareDates,
        aggregationFunction: AggregationFunction.SUM,
      })
        ?.then((data) => {
          let index = 0;
          if (data !== undefined) {
            data?.forEach((e) => {
              point = {
                name: index,
                value: Math.floor(e.value * 10) / 10,
                x: index,
                y: index,
                index,
              };
              utilityArray.push(point);
              index += 1;
            });
          }
          setCompareUtilityData(utilityArray);
        });
    }
  }, [compareDates.start, prevMetric]);
  class CustomFlyout extends React.Component {
    render() {
      const { x, y, datum } = this.props as any;
      const containerY: number = Math.max(4, y - 114);
      let containerX: number = x < 170 ? x + 75 : x - 115;
      if (compareToggled) {
        containerX = Math.min(660, Math.max(20, x - 165));
        return (
          <svg>
            <defs>
              <filter id="f3" x={-0.5} y={-1} width="200%" height="250%">
                <feOffset result="offOut" in="SourceGraphic" dx="0" dy="0" />
                <feColorMatrix
                  result="matrixOut"
                  in="offOut"
                  type="matrix"
                  values="0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0"
                />
                <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="4" />
                <feBlend in="SourceGraphic" in2="blurOut" mode="normal" result="colored" />
              </filter>
            </defs>
            {/* <line
              x1={x}
              y1={-100}
              x2={x}
              y2={500}
              stroke={Colors.light.hereworksDarkBlue}
              strokeDasharray={10}
            />
            <line
              x1={0}
              y1={y}
              x2={x + 1000}
              y2={y}
              stroke={Colors.light.hereworksDarkBlue}
              strokeDasharray={10}
            /> */}
            <rect
              x={containerX}
              y={containerY}
              width={330}
              height={104}
              stroke={Colors.light.hereworksDarkBlue}
              rx={5}
              fill="white"
              filter="url(#f3)"
            />
            <rect
              x={containerX + 12}
              y={containerY + 8}
              width={133}
              height={88}
              rx={4}
              fill={Colors.light.hereworksWhite100}
            />
            <rect
              x={containerX + 188}
              y={containerY + 8}
              width={133}
              height={88}
              rx={4}
              fill={Colors.light.hereworksYellow50}
            />
            <text
              x={containerX + 16}
              y={containerY + 24}
              fontFamily={'Poppins_500Medium'}
              fontSize={12}
              strokeWidth={0}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {tooltipDateLabel(graphData, datum.index, true)}
            </text>
            <text
              x={containerX + 32}
              y={containerY + 40}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {/* @ts-ignore */}
              {`${title}: ${graphData[datum.index]?.value ?? '-'}`}
            </text>
            <circle
              fill={Colors.light.hereworksPurple500}
              r={6}
              cx={containerX + 22}
              cy={containerY + 37}
            />
            <circle
              fill={Colors.light.hereworksDarkBlue}
              r={6}
              cx={containerX + 22}
              cy={containerY + 57}
            />
            <text
              x={containerX + 32}
              y={containerY + 60}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {/* @ts-ignore */}
              {`${prevTitle}: ${utilityData[datum.index]?.value ?? '-'}`}
            </text>
            {/* <circle
              fill={Colors.light.hereworksDarkBlue200}
              r={6}
              cx={x < 100 ? x - 35 : x - 143}
              cy={y - 37}
            /> */}
            {/* <text
              x={x < 100 ? x - 35 : x - 133}
              y={y - 34}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            > */}
            {/* @ts-ignore */}
            {/* {`Target Utility: ${utilityData[datum.x]?.targetValue}`} */}
            {/* </text> */}
            <svg width={16} height={16} fill="none" x={containerX + 157} y={containerY + 47}>
              <path
                d="M9 12H2m7 0-3 3m3-3L6 9M7 5h7M7 5l3 3M7 5l3-3"
                stroke="#2173C4"
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </svg>
            <text
              x={containerX + 192}
              y={containerY + 24}
              fontFamily={'Poppins_500Medium'}
              fontSize={12}
              strokeWidth={0}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {/* @ts-ignore */}
              {tooltipDateLabel(compareGraphData, datum.index, true)}
            </text>
            <text
              x={containerX + 212}
              y={containerY + 40}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {/* @ts-ignore */}
              {`${title}: ${compareGraphData[datum.index]?.value ?? '-'}`}
            </text>
            <circle fill={Colors.light.hereworksYellow800} r={6}
              cx={containerX + 202} cy={containerY + 37} />
            <circle fill={Colors.light.hereworksYellow500}
              r={6} cx={containerX + 202} cy={containerY + 57} />
            <text
              x={containerX + 212}
              y={containerY + 60}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            >
              {/* @ts-ignore */}
              {`${prevTitle}: ${compareUtilityData[datum.index]?.value ?? '-'}`}
            </text>
            {/* <circle fill={Colors.light.hereworksDarkBlue200}
             r={6} cx={x + 37} cy={y - 37} /> */}
            {/* <text
              x={x + 47}
              y={y - 34}
              fontFamily="sans-serif"
              fontSize={12}
              strokeWidth={0.3}
              fill={'#696D70'}
              stroke={'#696D70'}
            > */}
            {/* @ts-ignore */}
            {/* {`Target Utility: ${compareUtilityData[datum.x]?.targetValue}`} */}
            {/* </text> */}
          </svg>
        );
      }
      return (
        <svg>
          <defs>
            <filter id="f3" x={-0.5} y={-1} width="200%" height="250%">
              <feOffset result="offOut" in="SourceGraphic" dx="0" dy="0" />
              <feColorMatrix
                result="matrixOut"
                in="offOut"
                type="matrix"
                values="0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0"
              />
              <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="4" />
              <feBlend in="SourceGraphic" in2="blurOut" mode="normal" result="colored" />
            </filter>
          </defs>
          {/* todo: ask about lines -> */}
          {/* <line
            x1={x}
            y1={-100}
            x2={x}
            y2={500}
            stroke={Colors.light.hereworksDarkBlue}
            strokeDasharray={10}
          />
          <line
            x1={0}
            y1={y}
            x2={x + 1000}
            y2={y}
            stroke={Colors.light.hereworksDarkBlue}
            strokeDasharray={10}
          /> */}
          <rect
            x={containerX - 60}
            y={containerY}
            width={157}
            height={104}
            stroke={Colors.light.hereworksDarkBlue}
            rx={5}
            fill="white"
            filter="url(#f3)"
          />
          <text
            x={containerX - 55}
            y={containerY + 29}
            fontFamily={'Poppins_500Medium'}
            fontSize={12}
            strokeWidth={0}
            fill={'#696D70'}
            stroke={'#696D70'}
          >
            {tooltipDateLabel(graphData, datum.index, true)}
          </text>
          <text
            x={containerX - 35}
            y={containerY + 45}
            fontFamily="sans-serif"
            fontSize={12}
            strokeWidth={0.3}
            fill={'#696D70'}
            stroke={'#696D70'}
          >
            {/* @ts-ignore */}
            {`${title}: ${graphData[datum.index]?.value ?? '-'}`}
          </text>
          <circle
            fill={Colors.light.hereworksPurple500}
            r={6}
            cx={containerX - 45}
            cy={containerY + 42}
          />
          <circle
            fill={Colors.light.hereworksDarkBlue}
            r={6}
            cx={containerX - 45}
            cy={containerY + 62}
          />
          <text
            x={containerX - 35}
            y={containerY + 65}
            fontFamily="sans-serif"
            fontSize={12}
            strokeWidth={0.3}
            fill={'#696D70'}
            stroke={'#696D70'}
          >
            {/* @ts-ignore */}
            {`${prevTitle}: ${utilityData[datum.index]?.value ?? '-'}`}
          </text>
          {/* <circle
            fill={Colors.light.hereworksDarkBlue200}
            r={6}
            cx={x < 100 ? x - 35 : x - 63}
            cy={y - 42}
          /> */}
          {/* <text
            x={x < 100 ? x - 35 : x - 53}
            y={y - 39}
            fontFamily="sans-serif"
            fontSize={12}
            strokeWidth={0.3}
            fill={'#696D70'}
            stroke={'#696D70'}
          > */}
          {/* @ts-ignore */}
          {/* {`Target Utility: ${utilityData[datum.x]?.targetValue}`} */}
          {/* </text> */}
        </svg>
      );
    }
  }
  const chartTheme = {
    axis: {
      stroke: 'none',
      style: {
        stroke: '#696D70',
        tickLabels: {
          fill: '#696D70',
        },
        grid: { stroke: 'none' },
      },
    },
  };
  return (
    <View
      style={{ paddingStart: 16, height: 500 }}
      onLayout={(event) => setDimensions({
        width: event.nativeEvent.layout.width,
        height: event.nativeEvent.layout.height,
      })}>
      <VictoryChart
        singleQuadrantDomainPadding={{ x: false }}
        width={dimensions.width}
        height={dimensions.height}
        theme={chartTheme}
        domainPadding={{ x: [20, 20], y: [0, 0] }}
        domain={{ y: [0, higherDomainLimit] }}
        padding={{
          top: 10,
          left: 80,
          right: 100,
          bottom: 60,
        }}
        containerComponent={
          <VictoryVoronoiContainer
            voronoiDimension="x"
            labels={({ datum }) => datum}
            labelComponent={<CustomFlyout />}
          />
        }
      >
        <VictoryAxis
          dependentAxis
          crossAxis={false}
          offsetX={60}
          tickCount={9}
          tickFormat={(value: number) => yAxisTickFormat(value, domainLimit)}
          fixLabelOverlap={true}
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              fontSize: 14,
              marginRight: 15,
            },
            grid: {
              stroke: Colors.light.hereworksWhite500,
              strokeWidth: 1,
              pointerEvents: 'none',
              transform: 'translateX(3%)',
            },
          }}
          tickLabelComponent={
            <VictoryLabel dx={10}
              style={{
                fill: Colors.light.hereworksBlack400,
                fontSize: 14,
                strokeWidth: 0,
                fontFamily: 'Poppins_400Regular',
                textAnchor: 'end',
              }} />}
          label={title}
          axisLabelComponent={
            <VictoryLabel
              labelPlacement="parallel"
              dy={-10}
              style={{
                fill: alternateLineColor,
                fontSize: 14,
                strokeWidth: 0,
                fontFamily: 'Poppins_400Regular',
              }}
            />
          }
        />
        <VictoryAxis
          dependentAxis
          crossAxis={false}
          offsetX={dimensions.width}
          tickCount={9}
          tickFormat={(value: number) => yAxisTickFormat(value, alternateDomainLimit)}
          fixLabelOverlap={true}
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              fontSize: 14,
              marginRight: 15,
            },
          }}
          tickLabelComponent={
            <VictoryLabel
              style={{
                fill: Colors.light.hereworksBlack400,
                fontSize: 14,
                strokeWidth: 0,
                fontFamily: 'Poppins_400Regular',
                textAnchor: 'end',
              }}
              dx={-60}
            />
          }
          label={prevTitle ?? prevTitle}
          axisLabelComponent={<VictoryLabel
            dy={10}
            style={{
              fill: selectedLineColor,
              fontSize: 14,
              strokeWidth: 0,
              fontFamily: 'Poppins_400Regular',
            }}
          />} />
        <VictoryAxis
          name="axis1"
          tickValues={tickVals}
          fixLabelOverlap={true}
          tickFormat={(x: number) => axisDateLabel(graphData?.length > utilityData?.length
            ? graphData : utilityData, x)}
          tickCount={7}
          style={{
            axis: { stroke: 'none' },
          }}
          axisLabelComponent={
            <VictoryLabel
              dx={-430}
              dy={-5}
              style={{
                stroke: Colors.light.hereworksBlack400,
                fontSize: 14,
                strokeWidth: 0,
                fontFamily: 'Poppins_400Regular',
              }}
            />
          }
          tickLabelComponent={
            <VictoryLabel
              dy={18}
              style={{
                fontSize: 14,
                strokeWidth: 0,
                stroke: Colors.light.hereworksBlack400,
                fontFamily: 'Poppins_400Regular',
              }}
            />
          }
        />
        {!compareToggled && (
          <VictoryLine
            style={{
              data: {
                // eslint-disable-next-line max-len
                stroke: ({ active }) => (active ? Colors.light.hereworksDarkBlue : Colors.light.hereworksDarkBlue300),
                strokeWidth: 2,
              },
            }}
            x={'index'}
            y={'value'}
            data={utilityData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
            }))}
          />
        )}
        {!compareToggled && (
          <VictoryScatter
            x="index"
            y="value"
            data={utilityData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
            }))}
            size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
            style={{
              data: { fill: Colors.light.hereworksDarkBlue },
            }}
          />
        )}
        {compareToggled && (
          <VictoryGroup standalone={true}>
            <VictoryLine
              style={{
                data: {
                  // eslint-disable-next-line max-len
                  stroke: ({ active }) => (active ? Colors.light.hereworksDarkBlue : Colors.light.hereworksDarkBlue300),
                  strokeWidth: 2,
                },
              }}
              x={'index'}
              y={'value'}
              data={utilityData?.map((p) => ({
                ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
              }))}
            />
            <VictoryLine
              style={{
                data: {
                  stroke: Colors.light.hereworksYellow500,
                  strokeWidth: 2,
                },
              }}
              x={'index'}
              y={'value'}
              data={compareUtilityData?.map((p) => ({
                ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
              }))}
            />
          </VictoryGroup>
        )}
        <VictoryLine
          x="index"
          y="value"
          data={graphData?.map((p) => ({
            ...p, value: p.value * (higherDomainLimit / domainLimit),
          }))}
          interpolation="linear"
          style={{
            data: { stroke: Colors.light.hereworksPurple500, strokeWidth: 2 },
          }}
        />
        <VictoryScatter
          name="mainScatter"
          x="index"
          y="value"
          data={graphData?.map((p) => ({
            ...p, value: p.value * (higherDomainLimit / domainLimit),
          }))}
          size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
          style={{
            data: { fill: Colors.light.hereworksPurple500 },
          }}
        // dataComponent={<CustomPoint />} Sample for Map Spike
        />
        {compareToggled && (
          <VictoryLine
            x="index"
            y="value"
            data={compareGraphData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / domainLimit),
            }))}
            interpolation="linear"
            style={{
              data: { stroke: Colors.light.hereworksYellow800, strokeWidth: 2 },
            }}
          />
        )}
        {compareToggled && (
          <VictoryScatter
            name="compareScatter"
            data={compareGraphData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / domainLimit),
            }))}
            x="index"
            y="value"
            size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
            style={{
              data: { fill: Colors.light.hereworksYellow800 },
            }}
          />
        )}
        {compareToggled && (
          <VictoryScatter
            data={compareUtilityData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
            }))}
            x="index"
            y="value"
            size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
            style={{
              data: { fill: Colors.light.hereworksYellow500 },
            }}
          />
        )}
        {compareToggled && (
          <VictoryScatter
            data={utilityData?.map((p) => ({
              ...p, value: p.value * (higherDomainLimit / alternateDomainLimit),
            }))}
            x="index"
            y="value"
            size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
            style={{
              data: { fill: Colors.light.hereworksDarkBlue },
            }}
          />
        )}
        {compareToggled && (
          <VictoryAxis
            name="axis2"
            fixLabelOverlap={true}
            tickValues={tickVals2}
            tickFormat={(x) => axisDateLabel(compareGraphData, x)}
            tickCount={7}
            domainPadding={{ x: [0, 0], y: 20 }}
            style={{
              axis: { stroke: 'none' },
            }}
            axisLabelComponent={
              <VictoryLabel
                dx={-430}
                dy={11}
                style={{
                  fill: Colors.light.hereworksYellow500,
                  fontSize: 14,
                  strokeWidth: 0,
                  fontFamily: 'Poppins_400Regular',
                }}
              />
            }
            tickLabelComponent={
              <VictoryLabel
                dy={33}
                style={{
                  fill: Colors.light.hereworksYellow500,
                  fontSize: 14,
                  strokeWidth: 0,
                  fontFamily: 'Poppins_400Regular',
                }}
              />
            }
          />
        )}
      </VictoryChart>
      {!graphData.length && !utilityData.length
        && (!compareToggled || (!compareGraphData.length && !compareUtilityData.length))
        && (
          <View
            style={{
              position: 'absolute',
              alignSelf: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Text
              style={{
                color: Colors.light.hereworksBlack400,
                fontFamily: 'Roboto_400Regular',
                fontSize: 16,
              }}
            >
              No data available
            </Text>
          </View>
        )}
    </View>
  );
};

export default BarWithLineGraphWeb;
